/**
 * Receipt line → Product / Feature matching (Phase 9D, spec §33A) — PURE.
 *
 * Deterministic evidence first, and a suggestion is ONLY ever a
 * suggestion: nothing here confirms anything. A vague receipt description
 * must never become a fake exact match, so weak evidence is labeled weak
 * and the reviewer decides.
 */

export type SuggestionStrength = "exact" | "strong" | "weak" | "none";

export interface ProductCandidate {
  id: string;
  name: string;
  brand: string;
  sku: string;
  materialDefinitionId: string;
  /** Provider identity, when the product was discovered live (Phase 9B.1). */
  externalProductId: string | null;
  /** Attributes we can match on, e.g. UPC. */
  upc: string | null;
  /** True when this project already selected the product for a requirement. */
  selectedInProject: boolean;
  /** Features whose requirements use this product (BOM context). */
  featureIds: string[];
}

export interface LineEvidence {
  description: string;
  sku: string;
  upc: string;
  /** Provider product id if the document carried one (rare on receipts). */
  externalProductId: string | null;
}

export interface MatchSuggestion {
  productId: string | null;
  featureId: string | null;
  strength: SuggestionStrength;
  /** Why — shown to the reviewer verbatim. */
  basis: string;
}

const norm = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, " ");
/** SKUs differ only by punctuation across systems ("1001-234-567"). */
const normId = (s: string): string => s.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

/** Word overlap, ignoring noise words — a weak signal, labeled as such. */
function descriptionScore(a: string, b: string): number {
  const stop = new Set(["the", "and", "in", "with", "for", "of", "x", "w", "l", "h", "ft", "in."]);
  const words = (s: string) => new Set(norm(s).split(/[^a-z0-9.]+/).filter((w) => w.length > 2 && !stop.has(w)));
  const A = words(a);
  const B = words(b);
  if (A.size === 0 || B.size === 0) return 0;
  let hits = 0;
  for (const w of A) if (B.has(w)) hits++;
  return hits / Math.min(A.size, B.size);
}

/**
 * Suggest a product for one receipt line. Returns "none" rather than a
 * guess when nothing is convincing — an unmatched line is a legitimate,
 * expected outcome.
 */
export function suggestProduct(line: LineEvidence, candidates: ProductCandidate[]): MatchSuggestion {
  if (candidates.length === 0) {
    return { productId: null, featureId: null, strength: "none", basis: "No catalog products to match against." };
  }

  // --- exact, deterministic identity -------------------------------
  if (line.externalProductId) {
    const hit = candidates.find((c) => c.externalProductId && normId(c.externalProductId) === normId(line.externalProductId!));
    if (hit) return pick(hit, "exact", `Matched on the supplier's product id ${line.externalProductId}.`);
  }
  if (line.upc.trim() !== "") {
    const hit = candidates.find((c) => c.upc && normId(c.upc) === normId(line.upc));
    if (hit) return pick(hit, "exact", `Matched on UPC ${line.upc}.`);
  }
  if (line.sku.trim() !== "") {
    const bySku = candidates.filter((c) => c.sku && normId(c.sku) === normId(line.sku));
    if (bySku.length === 1) return pick(bySku[0], "exact", `Matched on SKU/model ${line.sku}.`);
    if (bySku.length > 1) {
      return { productId: null, featureId: null, strength: "none", basis: `SKU ${line.sku} matches ${bySku.length} products — choose which one.` };
    }
    // A receipt SKU that appears inside a product's id is strong, not exact.
    const contains = candidates.filter((c) => c.externalProductId && normId(c.externalProductId).includes(normId(line.sku)));
    if (contains.length === 1) return pick(contains[0], "strong", `The receipt's item number ${line.sku} appears in this product's supplier id.`);
  }

  // --- softer evidence, always labeled weak -------------------------
  const scored = candidates
    .map((c) => ({ c, score: descriptionScore(line.description, `${c.brand} ${c.name}`) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || Number(b.c.selectedInProject) - Number(a.c.selectedInProject));
  if (scored.length === 0) {
    return { productId: null, featureId: null, strength: "none", basis: "Nothing in the catalog resembles this line." };
  }
  const best = scored[0];
  const runnerUp = scored[1];
  const clear = !runnerUp || best.score - runnerUp.score >= 0.25;
  if (best.score >= 0.6 && clear && best.c.selectedInProject) {
    return pick(best.c, "strong", "Description matches a product this project already selected.");
  }
  if (best.score >= 0.5 && clear) {
    return pick(best.c, "weak", "Description looks similar — please confirm before importing.");
  }
  return {
    productId: null,
    featureId: null,
    strength: "none",
    basis: "No confident match — choose a product or leave this line unmatched.",
  };
}

function pick(c: ProductCandidate, strength: SuggestionStrength, basis: string): MatchSuggestion {
  return {
    productId: c.id,
    // Only propose a feature when the product serves exactly one; a
    // product used in several places must be chosen by the user.
    featureId: c.featureIds.length === 1 ? c.featureIds[0] : null,
    strength,
    basis: c.featureIds.length > 1 ? `${basis} It is used by ${c.featureIds.length} items — choose which one.` : basis,
  };
}

/** Suggestions below this strength must never be pre-confirmed. */
export function isAutoConfirmable(strength: SuggestionStrength): boolean {
  return strength === "exact";
}
